import React, { useEffect, useMemo, useState } from "react";
import { api, urls } from "../../api/api";
import { Container, Row, Col, Card, CardBody, Table, Badge, Button } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import PaginationBar from "../../components/Common/PaginationBar";

const badgeColor = (st) => {
  const map = {
    en_attente: "warning",
    confirme: "success",
    annule: "danger",
    reporte: "info",
    realise: "primary",
    no_show: "secondary",
  };
  return map[st] || "secondary";
};

export default function Dashboard() {
  document.title = "Dashboard";

  // ✅ role check
  const rawUser = localStorage.getItem("user") || localStorage.getItem("authUser");
  let currentUser = {};
  try {
    currentUser = rawUser ? JSON.parse(rawUser) : {};
  } catch {
    currentUser = {};
  }
  const isAdmin = currentUser?.role === "admin";

  const [stats, setStats] = useState({
    clients: null,
    services: null,
    agents: null,
    rdv_total: 0,
    rdv_today: 0,
    rdv_confirmed: 0,
    rdv_cancelled: 0,
  });

  const [chart, setChart] = useState({ labels: [], series: [] });
  const [nextRdvs, setNextRdvs] = useState([]);
  const [loading, setLoading] = useState(false);

  // pagination prochains rdv
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(urls.dashboard);
      setStats(r.data?.stats || {});
      setChart(r.data?.chart || { labels: [], series: [] });
      const list = Array.isArray(r.data?.next_rdvs) ? r.data.next_rdvs : [];
      setNextRdvs(list);
      setPage(1);
    } catch (e) {
      alert(e?.response?.data?.message || "Erreur chargement dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const weeklyChartOptions = useMemo(() => {
    return {
      chart: { toolbar: { show: false } },
      xaxis: { categories: chart.labels || [] },
      dataLabels: { enabled: false },
      yaxis: { min: 0, forceNiceScale: true },
    };
  }, [chart.labels]);

  const weeklyChartSeries = useMemo(() => {
    return [{ name: "RDV", data: chart.series || [] }];
  }, [chart.series]);

  // pagination computed
  const totalItems = nextRdvs.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(page, totalPages);

  const paginatedNext = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return nextRdvs.slice(start, start + pageSize);
  }, [nextRdvs, safePage, pageSize]);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPages]);

  const exportNextRdvsCsv = () => {
    const rows = (nextRdvs || []).map((r) => ({
      id: r.id,
      start: r.start,
      client: r.client?.full_name || "",
      service: r.service?.name || "",
      agent: r.agent?.name || r.agent?.email || "",
      status: r.status || "",
    }));

    const header = ["id", "start", "client", "service", "agent", "status"];
    const escape = (v) => `"${String(v ?? "").replaceAll('"', '""')}"`;
    const csv =
      header.join(",") +
      "\n" +
      rows.map((x) => header.map((k) => escape(x[k])).join(",")).join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "dashboard_next_rdvs.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="page-content pt-0">
      <Container fluid className="pt-0">
        <Row className="mb-3">
          <Col className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0">Dashboard</h4>
            <Button color="secondary" onClick={load} disabled={loading}>
              {loading ? "..." : "Refresh"}
            </Button>
          </Col>
        </Row>

        {/* ✅ Row 1: RDV stats (admin + agent) */}
        <Row className="mt-0">
          <Col md={3}>
            <Card className="border border-primary mb-3">
              <CardBody className="py-3">
                <div className="text-muted">RDV (total)</div>
                <div className="h2 mb-0">{stats.rdv_total ?? 0}</div>
              </CardBody>
            </Card>
          </Col>

          <Col md={3}>
            <Card className="border border-info mb-3">
              <CardBody className="py-3">
                <div className="text-muted">RDV (Aujourd&apos;hui)</div>
                <div className="h2 mb-0">{stats.rdv_today ?? 0}</div>
              </CardBody>
            </Card>
          </Col>

          <Col md={3}>
            <Card className="border border-success mb-3">
              <CardBody className="py-3">
                <div className="text-muted">RDV Confirmés</div>
                <div className="h2 mb-0">{stats.rdv_confirmed ?? 0}</div>
              </CardBody>
            </Card>
          </Col>

          <Col md={3}>
            <Card className="border border-danger mb-3">
              <CardBody className="py-3">
                <div className="text-muted">RDV Annulés</div>
                <div className="h2 mb-0">{stats.rdv_cancelled ?? 0}</div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ✅ Row 2: admin-only company stats */}
        {isAdmin && (
          <Row>
            <Col md={4}>
              <Card className="border border-warning mb-3">
                <CardBody className="py-3">
                  <div className="text-muted">Clients</div>
                  <div className="h2 mb-0">{stats.clients ?? 0}</div>
                </CardBody>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="border border-dark mb-3">
                <CardBody className="py-3">
                  <div className="text-muted">Services</div>
                  <div className="h2 mb-0">{stats.services ?? 0}</div>
                </CardBody>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="border border-secondary mb-3">
                <CardBody className="py-3">
                  <div className="text-muted">Agents</div>
                  <div className="h2 mb-0">{stats.agents ?? 0}</div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* Row 3 */}
        <Row>
          <Col lg={6}>
            <Card className="mb-3">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">RDV cette semaine</h5>
                  <span className="text-muted" style={{ fontSize: 12 }}>
                    {loading ? "Chargement..." : ""}
                  </span>
                </div>

                <div className="mt-3">
                  <ReactApexChart options={weeklyChartOptions} series={weeklyChartSeries} type="bar" height={280} />
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={6}>
            <Card className="mb-3">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Prochains RDV</h5>
                  <Button color="success" size="sm" onClick={exportNextRdvsCsv} disabled={totalItems === 0}>
                    Export CSV
                  </Button>
                </div>

                <div className="table-responsive">
                  <Table className="table align-middle table-nowrap mb-0">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Heure</th>
                        <th>Client</th>
                        <th>Service</th>
                        <th>Agent</th>
                        <th>Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedNext.length === 0 ? (
                        <tr>
                          <td colSpan="6">Aucun RDV à venir</td>
                        </tr>
                      ) : (
                        paginatedNext.map((r) => {
                          const d = new Date(r.start);
                          const date = d.toLocaleDateString();
                          const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

                          return (
                            <tr key={r.id}>
                              <td>{date}</td>
                              <td>{time}</td>
                              <td>{r.client?.full_name || "-"}</td>
                              <td>{r.service?.name || "-"}</td>
                              <td>{r.agent?.name || r.agent?.email || "-"}</td>
                              <td>
                                <Badge color={badgeColor(r.status)} pill>
                                  {r.status}
                                </Badge>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>

                <PaginationBar
                  page={safePage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  pageSize={pageSize}
                  setPage={setPage}
                  setPageSize={setPageSize}
                  pageSizeOptions={[5, 10, 20]}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
